import {useRef, useState} from 'react';
import {Accordion, AccordionDetails, AccordionSummary, Alert, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Stack, TextField, Typography} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {useAppTranslation} from '@mybake/ui/hooks/useAppTranslation';
import {useAccountDeletion} from '../../models/AccountDeletion';
import {accountDeletionAPI} from '../../services/AccountDeletionServices';
import {getApiErrorMessage, normalizeApiError} from '../../common/apiErrors';

export function AccountDeletionActions({showExport = true}: {showExport?: boolean}) {
    const {t} = useAppTranslation();
    const deletion = useAccountDeletion();
    const [download, downloadState] = accountDeletionAPI.useDownloadAccountExportMutation({fixedCacheKey: 'account-export'});
    const [cancel, cancelState] = accountDeletionAPI.useCancelAccountDeletionMutation({fixedCacheKey: 'account-restore'});
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);
    const busy = useRef(false);
    const run = async (action: 'download' | 'cancel') => {
        if (busy.current) return;
        busy.current = true;
        setError(''); setSuccess(false);
        try {
            if (action === 'download') await download().unwrap();
            else { await cancel().unwrap(); setSuccess(true); }
        } catch (error) { setError(getApiErrorMessage(error, t)); }
        finally { busy.current = false; }
    };
    if (!deletion.isReadOnly) return null;
    return <Stack spacing={1}>
        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
            {showExport && <Button disabled={downloadState.isLoading || cancelState.isLoading} onClick={() => run('download')}
                startIcon={downloadState.isLoading ? <CircularProgress size={16}/> : undefined}>{t('settings:accountDeletion.export')}</Button>}
            {deletion.isReadOnly && <Button disabled={cancelState.isLoading || downloadState.isLoading} onClick={() => run('cancel')}
                startIcon={cancelState.isLoading ? <CircularProgress size={16}/> : undefined}>{t('settings:accountDeletion.restore')}</Button>}
        </Stack>
        {error && <Alert severity="error">{error}</Alert>}
        {success && <Alert severity="success">{t('settings:accountDeletion.restored')}</Alert>}
    </Stack>;
}

export function AccountDeletionNotice() {
    const {t, i18n} = useAppTranslation();
    const deletion = useAccountDeletion();
    if (!deletion.isReadOnly) return null;
    const date = deletion.scheduledDeletionAtUtc && new Date(deletion.scheduledDeletionAtUtc);
    return <Alert severity="warning" sx={{m: 2, position: 'sticky', top: '3rem', zIndex: 1100}}>
        {date && !Number.isNaN(date.getTime())
            ? t('settings:accountDeletion.scheduled', {date: date.toLocaleString(i18n.resolvedLanguage || i18n.language)})
            : t('settings:accountDeletion.readOnly')}
        <AccountDeletionActions/>
    </Alert>;
}

export default function AccountDeletionPanel() {
    const {t} = useAppTranslation();
    const deletion = useAccountDeletion();
    const [request, requestState] = accountDeletionAPI.useRequestAccountDeletionMutation();
    const [open, setOpen] = useState(false);
    const [password, setPassword] = useState('');
    const [passwordError, setPasswordError] = useState('');
    const [error, setError] = useState('');
    const submitting = useRef(false);
    const close = () => { if (!submitting.current) { setOpen(false); setPassword(''); setPasswordError(''); setError(''); requestState.reset(); } };
    const submit = async () => {
        if (!password || submitting.current) return;
        submitting.current = true;
        setError(''); setPasswordError('');
        const currentPassword = password;
        setPassword('');
        try { await request({password: currentPassword}).unwrap(); setOpen(false); }
        catch (error) {
            if (normalizeApiError(error).code === 'account.password_invalid') setPasswordError(t('settings:accountDeletion.passwordInvalid'));
            else setError(getApiErrorMessage(error, t));
        } finally { submitting.current = false; requestState.reset(); }
    };
    return <Accordion sx={{mt: 3, border: '1px solid', borderColor: 'error.main'}}>
        <AccordionSummary expandIcon={<ExpandMoreIcon/>}><Typography color="error">{t('settings:accountDeletion.dangerZone')}</Typography></AccordionSummary>
        <AccordionDetails><Stack spacing={2}>
            {deletion.isReadOnly ? <Typography>{t('settings:accountDeletion.pending')}</Typography>
                : <Button color="error" variant="outlined" onClick={() => setOpen(true)}>{t('settings:accountDeletion.delete')}</Button>}
            <AccountDeletionActions showExport={false}/>
        </Stack></AccordionDetails>
        <Dialog open={open} onClose={close} fullWidth maxWidth="sm">
            <DialogTitle>{t('settings:accountDeletion.delete')}</DialogTitle>
            <DialogContent><Stack spacing={2}>
                <DialogContentText>{t('settings:accountDeletion.explanation')}</DialogContentText>
                <TextField autoFocus type="password" autoComplete="current-password" label={t('settings:accountDeletion.password')}
                    value={password} onChange={event => setPassword(event.target.value)} error={!!passwordError} helperText={passwordError}
                    disabled={requestState.isLoading} inputProps={{style: {cursor: 'default'}}}/>
                {error && <Alert severity="error">{error}</Alert>}
            </Stack></DialogContent>
            <DialogActions><Button onClick={close} disabled={requestState.isLoading}>{t('common:cancel')}</Button>
                <Button color="error" disabled={!password || requestState.isLoading} onClick={submit}
                    startIcon={requestState.isLoading ? <CircularProgress size={16}/> : undefined}>{t('settings:accountDeletion.delete')}</Button></DialogActions>
        </Dialog>
    </Accordion>;
}
